import { useState } from 'react';
import { Plus, Mail, UserCircle2 } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardSubtitle, CardBody } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Alert } from '@/components/ui/Alert';
import { StatusBadge } from '@/components/ui/Badge';
import { useClientParents } from '@/api/parents';
import { useAuthStore } from '@/stores/auth';
import { type ClientRecord } from '@/api/clients';
import { AddParentDialog } from '../AddParentDialog';

/*
 * Portal Accounts tab — list + add parent-portal accounts attached to
 * this client. Creation goes PMA-client → PMA-server → admin-server
 * (which owns Cognito + admin DB) → back to PMA via /api/internal/users.
 *
 * Permission: any authenticated user can read the list (parents'
 * names + emails are practice-internal info, not PHI). Adding a
 * parent requires `manage_settings` — same gate Home uses for
 * adding workforce users today.
 */
export function ClientPortalAccountsTab({ client: c }: { client: ClientRecord }) {
    const canAdd = useAuthStore((s) => s.hasPermission('manage_settings'));
    const { data: parents = [], isLoading, isError, error } = useClientParents(c.id);
    const [addOpen, setAddOpen] = useState(false);

    return (
        <Card className="p-0 overflow-hidden">
            <CardHeader className="px-5 pt-5 flex items-start justify-between">
                <div>
                    <CardTitle>Portal Accounts</CardTitle>
                    <CardSubtitle>
                        Parents and guardians who can sign in to the parent portal to view this
                        client's invoices, sessions, and treatment plans.
                    </CardSubtitle>
                </div>
                {canAdd && (
                    <Button type="button" size="sm" variant="primary" onClick={() => setAddOpen(true)}>
                        <Plus className="w-3.5 h-3.5" /> Add parent
                    </Button>
                )}
            </CardHeader>
            <CardBody className="px-5 pb-5">
                {isLoading ? (
                    <p className="text-sm text-text-subtle">Loading…</p>
                ) : isError ? (
                    <Alert variant="danger">
                        {error instanceof Error ? error.message : 'Could not load portal accounts.'}
                    </Alert>
                ) : parents.length === 0 ? (
                    <p className="text-sm text-text-subtle">
                        No portal accounts yet.
                        {canAdd
                            ? ' Click "Add parent" to invite a parent or guardian.'
                            : ' A practice admin can add one from this page.'}
                    </p>
                ) : (
                    <ul className="divide-y divide-border">
                        {parents.map((p) => (
                            <li
                                key={p.id}
                                className="flex items-center justify-between gap-3 py-3"
                                data-testid="portal-account-row"
                            >
                                <div className="flex items-start gap-3 min-w-0">
                                    <UserCircle2 className="w-5 h-5 text-text-muted shrink-0 mt-0.5" aria-hidden />
                                    <div className="min-w-0">
                                        <div className="text-sm font-medium text-text truncate">
                                            {p.first} {p.last}
                                        </div>
                                        <div className="text-xs text-text-muted inline-flex items-center gap-1 mt-0.5">
                                            <Mail className="w-3 h-3" aria-hidden />
                                            <span className="truncate">{p.email}</span>
                                        </div>
                                    </div>
                                </div>
                                <StatusBadge status={p.status} />
                            </li>
                        ))}
                    </ul>
                )}
            </CardBody>

            {canAdd && (
                <AddParentDialog
                    clientId={c.id}
                    clientName={`${c.first ?? ''} ${c.last ?? ''}`.trim()}
                    open={addOpen}
                    onOpenChange={setAddOpen}
                />
            )}
        </Card>
    );
}
